import React from 'react';

import {
  Button,
  Divider,
  Grid,
  styled,
  Typography
} from '@mui/material';
import {
  enumCache,
  fields,
  InvestmentAreaMultiSelectField,
  InvestmentCategorySelectField,
  InvestmentGeographyGroupedMultiSelectField,
  InvestmentStageMultiSelectField,
  ProfileSection,
  USStateSelectField,
  authService,
  RequiredFieldLabel
} from 'modules';

import * as dataService from './dataService';



const ControlledTextField = fields.genTextFieldControllerComponent<dataService.IFormState>();
const InvestmentCategorySelectFieldController = fields.genEnumSelectFieldControllerComponent<dataService.IFormState, enumCache.IInvestmentCategory>(InvestmentCategorySelectField({
  label: 'Category'
}));
const InvestmentStageMultiSelectFieldController = fields.genEnumMultiSelectFieldControllerComponent<dataService.IFormState, enumCache.IInvestmentStage>(InvestmentStageMultiSelectField({
  label: 'Company/Investment Stage'
}));
const InvestmentGeographyGroupedMultiSelectFieldController = fields.genGroupedEnumMultiSelectFieldControllerComponent<dataService.IFormState, enumCache.IInvestmentGeography>(InvestmentGeographyGroupedMultiSelectField({
  label: 'Investment Geography (for investors only)'
}));
const InvestmentAreaMultiSelectFieldController = fields.genEnumMultiSelectFieldControllerComponent<dataService.IFormState, string>(InvestmentAreaMultiSelectField({
  label: 'Company/Investment Area'
}));
const USStateSelectFieldController = fields.genEnumSelectFieldControllerComponent<dataService.IFormState, string>(USStateSelectField({
  label: 'State'
}));
const SubSectionTypography = styled(Typography)(() => ({
  color: '#1E3949',
  fontFamily: 'Noto Serif',
  fontSize: '18px'
}));



export interface IEditProfileSectionProps { }

export const EditProfileSection: React.FC<IEditProfileSectionProps> = props => {
  const [authLoading, setAuthLoading] = React.useState(false);
  const {
    form,
    loading: mutationLoading,
    mutations,
    data
  } = dataService.useEditProfileSection();
  const loading = mutationLoading || authLoading;



  async function processSubmit(formData: dataService.IFormState) {
    await mutations.saveMyProfile(formData);
    setAuthLoading(true);
    await authService.updateEmail(formData.email);
    setAuthLoading(false);
  }



  return (
    <ProfileSection.FC title="Edit Profile">
      <form onSubmit={form.handleSubmit(processSubmit)}>
        <Grid
          container
          spacing={5}>
          <Grid
            item
            xs={6}>
            <ControlledTextField
              fieldKey="firstName"
              label={<RequiredFieldLabel.FC text="First Name" />}
              formControl={form.control}
              isDisabled={loading} />
          </Grid>

          <Grid
            item
            xs={6}>
            <ControlledTextField
              fieldKey="lastName"
              label={<RequiredFieldLabel.FC text="Last Name" />}
              formControl={form.control}
              isDisabled={loading} />
          </Grid>

          <Grid
            item
            xs={6}>
            <ControlledTextField
              fieldKey="websiteUrl"
              label="Website"
              formControl={form.control}
              isDisabled={loading} />
          </Grid>

          <Grid
            item
            xs={6}>
            <ControlledTextField
              fieldKey="companyName"
              label={<RequiredFieldLabel.FC text="Company Name" />}
              formControl={form.control}
              isDisabled={loading} />
          </Grid>

          <Grid
            item
            xs={6}>
            <ControlledTextField
              fieldKey="email"
              label={<RequiredFieldLabel.FC text="Email" />}
              formControl={form.control}
              isDisabled={loading} />
          </Grid>

          <Grid
            item
            xs={6}>
            <ControlledTextField
              fieldKey="phoneNumber"
              label="Phone Number"
              formControl={form.control}
              isDisabled={loading} />
          </Grid>

          <Grid
            item
            xs={6}>
            <InvestmentCategorySelectFieldController
              fieldKey="investmentCategories"
              formControl={form.control}
              isDisabled={loading} />
          </Grid>

          <Grid
            item
            xs={6}>
            <InvestmentAreaMultiSelectFieldController
              fieldKey="investmentAreas"
              formControl={form.control}
              isDisabled={loading} />
          </Grid>

          <Grid
            item
            xs={6}>
            <InvestmentStageMultiSelectFieldController
              fieldKey="investmentStages"
              formControl={form.control}
              isDisabled={loading} />
          </Grid>

          <Grid
            item
            xs={6}>
            <InvestmentGeographyGroupedMultiSelectFieldController
              fieldKey="investmentGeographies"
              formControl={form.control}
              isDisabled={loading} />
          </Grid>

          <Grid
            item
            xs={12}>
            <ControlledTextField
              fieldKey="companyDescription"
              label="Company Description"
              formControl={form.control}
              isDisabled={loading}
              numLines={8} />
          </Grid>

          {data.isInvestor && (
            <>
              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid
                item
                xs={12}>
                <SubSectionTypography>
                  Check Size
                </SubSectionTypography>
              </Grid>

              <Grid
                item
                xs={6}>
                <ControlledTextField
                  fieldKey="checkSizeMinDollars"
                  type="number"
                  label="Low"
                  formControl={form.control}
                  isDisabled={loading} />
              </Grid>

              <Grid
                item
                xs={6}>
                <ControlledTextField
                  fieldKey="checkSizeMaxDollars"
                  label="High"
                  formControl={form.control}
                  type="number"
                  isDisabled={loading} />
              </Grid>
            </>
          )}

          <Grid
            item
            xs={12}>
            <Divider />
          </Grid>

          <Grid
            item
            xs={12}>
            <SubSectionTypography>
              Location
            </SubSectionTypography>
          </Grid>

          <Grid
            item
            xs={4}>
            <ControlledTextField
              fieldKey="addressCity"
              label={<RequiredFieldLabel.FC text="City" />}
              isDisabled={loading}
              formControl={form.control} />
          </Grid>

          <Grid
            item
            xs={2}>
            <USStateSelectFieldController
              fieldKey="addressUsState"
              formControl={form.control}
              isDisabled={loading} />
          </Grid>

          <Grid
            item xs={2}>
            <ControlledTextField
              fieldKey="addressCountry"
              label={<RequiredFieldLabel.FC text="Country" />}
              isDisabled={loading}
              formControl={form.control} />
          </Grid>

          <Grid
            item
            xs={12}>
            <Button
              type="submit"
              disabled={loading}>
              Update Profile
            </Button>
          </Grid>

        </Grid>
      </form>

    </ProfileSection.FC>
  );
};
